import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import styles from "./horizontalBarChart.module.scss";

interface HorizontalBarChartProps {
  title?: string;
  data?: any[];
  nameKey?: string;
  valueKey?: string;
  barColor?: string;
  height?: number;
  valueLabel?: string;
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  title = "Horizontal Bar Chart",
  data = [],
  nameKey = "name",
  valueKey = "value",
  barColor = "#5b6b9e",
  height = 280,
  valueLabel = "",
}) => {
  // Transform and normalize data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => ({
      name: item[nameKey] || item.group || item.name || item.label || `Item ${index + 1}`,
      value: Number(item[valueKey] || item.value || 0),
    }));
  }, [data, nameKey, valueKey]);

  // Calculate dynamic domain and ticks based on data
  const { maxValue, ticks } = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { maxValue: 100, ticks: [0, 25, 50, 75, 100] };
    }

    const max = Math.max(...chartData.map((item) => item.value), 1);
    const roundedMax = Math.ceil(max / 10) * 10 + 20;
    const tickInterval = Math.ceil(roundedMax / 4 / 10) * 10;
    const calculatedTicks = Array.from(
      { length: 5 },
      (_, i) => i * tickInterval
    );

    return { maxValue: roundedMax, ticks: calculatedTicks };
  }, [chartData]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>
            {payload[0].payload.name}
          </Typography>
          <Typography className={styles.tooltipValue}>
            {payload[0].value} {valueLabel}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.chartContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={height}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
            barCategoryGap={20} 
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              type="number"
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              domain={[0, maxValue]}
              ticks={ticks}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              width={120}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            <Bar
              dataKey="value"
              fill={barColor}
              radius={[0, 4, 4, 0]}
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default HorizontalBarChart;
